import {
  GetCreditCost,
  PERSON_UPDATE_BREAKDOWN_TOOLTIP,
} from '@aibees/elephant/constants';
// Straight from hooks/types, not the `@aibees/elephant/hooks` barrel: that
// barrel re-exports hooks which import components, and this module is itself
// reached through the components barrel.
import {
  PersonUpdateBreakdownKey,
  SaveRecordAction,
} from '@aibees/elephant/hooks/types';
import { IconType } from '@aibees/shared/components';
import { CreditUsageType } from '@aibees/shared/definition';
import { fCredit } from '@aibees/shared/utils';
import { EnrichButtonDialogType } from './types';

export type { GetCreditCost };

export interface BulkUpdateActionConfig {
  label: string;
  icon: IconType;
  // Sent to both ?action=cost-estimation and ?action=store.
  actions: SaveRecordAction[];
  // Breakdown keys this action can produce, in display order. Anything else the
  // server returns is ignored.
  rowKeys: PersonUpdateBreakdownKey[];
  // Info-icon copy in the menu, built against the live rates.
  tooltip: (getCost: GetCreditCost) => string;
  // Why Update is disabled once the preflight finds nothing eligible.
  emptyHelperText: string;
}

/**
 * The Saved Records → Enrich menu. One entry per update action; the menu, the
 * preflight and the submit payload are all driven from here.
 *
 * Update Personality Analysis (CONTACT_UPDATE_ANALYSIS, priced by
 * CreditUsageType.CONTACT_ANALYSIS_UPDATE) is specified in §7 but its back-end
 * action is not ready, so it is absent — along with the PERSONALITY_ANALYSIS row
 * on Update All and the parts of the §7.2 copy that describe it.
 * See docs/saved-records-enrich-actions.md.
 */
export const BULK_UPDATE_ACTIONS: Record<
  EnrichButtonDialogType,
  BulkUpdateActionConfig
> = {
  [EnrichButtonDialogType.UpdateAll]: {
    label: 'Update All',
    icon: 'sync-outline',
    actions: [
      SaveRecordAction.CONTACT_UPDATE_JOB_CHANGE,
      SaveRecordAction.CONTACT_UPDATE_PROFILE,
    ],
    rowKeys: [
      PersonUpdateBreakdownKey.WORK_HISTORY,
      PersonUpdateBreakdownKey.PROFILE_INSIGHTS,
    ],
    tooltip: (getCost) =>
      `Updates all eligible saved-record changes in one run: Work History / Job Change and Profile Insights. Locked or current data is skipped. Profile updates cost ${fCredit(
        getCost(CreditUsageType.CONTACT_PROFILE_UPDATE)
      )} credits once per successfully updated latest profile version, even when both Work History / Job Change and Profile Insights changed.`,
    emptyHelperText:
      "The selected contacts have no pending updates and don't need updating.",
  },
  [EnrichButtonDialogType.UpdateJobChange]: {
    label: 'Update Job Changes',
    icon: 'briefcase-arrow-left-right-outline',
    actions: [SaveRecordAction.CONTACT_UPDATE_JOB_CHANGE],
    rowKeys: [PersonUpdateBreakdownKey.WORK_HISTORY],
    // Same copy as the WORK_HISTORY row in every preflight dialog.
    tooltip: (getCost) =>
      PERSON_UPDATE_BREAKDOWN_TOOLTIP[PersonUpdateBreakdownKey.WORK_HISTORY]?.(
        getCost
      ) ?? '',
    emptyHelperText:
      "The job changes for selected contacts are processed and don't need updating.",
  },
  [EnrichButtonDialogType.UpdateProfileInsights]: {
    label: 'Update Profile Insights',
    icon: 'refresh-user',
    actions: [SaveRecordAction.CONTACT_UPDATE_PROFILE],
    rowKeys: [PersonUpdateBreakdownKey.PROFILE_INSIGHTS],
    // Same copy as the PROFILE_INSIGHTS row in every preflight dialog.
    tooltip: (getCost) =>
      PERSON_UPDATE_BREAKDOWN_TOOLTIP[
        PersonUpdateBreakdownKey.PROFILE_INSIGHTS
      ]?.(getCost) ?? '',
    emptyHelperText:
      "The selected contacts profile insights are already up-to-date and don't need updating.",
  },
};

// Menu order.
export const BULK_UPDATE_ACTION_ORDER: EnrichButtonDialogType[] = [
  EnrichButtonDialogType.UpdateAll,
  EnrichButtonDialogType.UpdateJobChange,
  EnrichButtonDialogType.UpdateProfileInsights,
];
